import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Envelope, ChatCircleDots, FloppyDisk, PaperPlaneRight, Eye, EyeSlash, UserCirclePlus, PencilSimple, Trash, Users, ShieldCheck, Plus, X, ListBullets } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('integrations');

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templLoading, setTemplLoading] = useState(false);
  const [templForm, setTemplForm] = useState({ naziv: '', naslov: '', poruka: '', kanal: 'email', jezik: 'sr' });
  const [editingTemplId, setEditingTemplId] = useState(null);
  const [showTemplForm, setShowTemplForm] = useState(false);
  const [cfgJezici, setCfgJezici] = useState(['sr', 'al', 'bs']);
  const [loading, setLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingViber, setSavingViber] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);

  // Agency info (per-tenant: name, address, signature)
  const [agencyForm, setAgencyForm] = useState({
    naziv: '', adresa: '', grad: '', telefon: '', mobilni: '', email: '', web: '',
    potpisnik_ime: '', potpisnik_titula: 'ovlašćeni računovođa',
    pib: '', direktor_jmbg: '', ziro_racun: ''
  });
  const [savingAgency, setSavingAgency] = useState(false);

  const [emailForm, setEmailForm] = useState({
    gmail_email: '', gmail_app_password: '', enabled: false,
    smtp_host: '', smtp_port: 0, smtp_secure: '',
    imap_host: '', imap_port: 993, sent_folder: ''
  });
  const [viberForm, setViberForm] = useState({
    viber_auth_token: '', viber_sender_name: 'FINANCE GROUP', infobip_base_url: '', infobip_sender: 'IBSelfServe', enabled: false, paused: false
  });

  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '', email: '', password: '', role: 'employee'
  });

  // Config lists state
  const [configLists, setConfigLists] = useState({ jezici: [], vrste_dokumenata: [], vrste_posla: [], vjere: [], nacionalnosti: [], radna_mjesta: [], irms_predmeti: [] });
  const [configLoading, setConfigLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newItems, setNewItems] = useState({ jezici: '', vrste_dokumenata: '', vrste_posla: '', vjere: '', nacionalnosti: '' });

  useEffect(() => { fetchSettings(); }, []);
  useEffect(() => { if (activeTab === 'users') fetchUsers(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'config') fetchConfigLists(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'templates') fetchTemplates(); }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setEmailForm(response.data.email);
      setViberForm(response.data.viber);
      if (response.data.agency) setAgencyForm(prev => ({ ...prev, ...response.data.agency }));
    } catch (error) {
      toast.error('Greska pri ucitavanju podesavanja');
    } finally { setLoading(false); }
  };

  const saveAgency = async () => {
    setSavingAgency(true);
    try {
      await api.put('/settings/agency', agencyForm);
      toast.success('Podaci agencije sačuvani');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška');
    } finally { setSavingAgency(false); }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Greska pri ucitavanju korisnika');
    } finally { setUsersLoading(false); }
  };

  const fetchConfigLists = async () => {
    setConfigLoading(true);
    try {
      const r = await api.get('/settings/config-lists');
      setConfigLists(r.data);
    } catch { toast.error('Greška pri učitavanju konfiguracije'); }
    finally { setConfigLoading(false); }
  };

  const saveConfigLists = async () => {
    setSavingConfig(true);
    try {
      await api.put('/settings/config-lists', configLists);
      toast.success('Konfiguracione liste sačuvane');
    } catch { toast.error('Greška'); }
    finally { setSavingConfig(false); }
  };

  const addConfigItem = (key) => {
    const val = newItems[key].trim();
    if (!val) return;
    if (configLists[key].includes(val)) { toast.error('Već postoji'); return; }
    setConfigLists(prev => ({ ...prev, [key]: [...prev[key], val] }));
    setNewItems(prev => ({ ...prev, [key]: '' }));
  };

  const removeConfigItem = (key, idx) => {
    setConfigLists(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  };

  const fetchTemplates = async () => {
    setTemplLoading(true);
    try {
      const [r, c] = await Promise.all([api.get('/settings/templates'), api.get('/settings/config-lists')]);
      setTemplates(r.data);
      setCfgJezici(c.data.jezici || ['sr', 'al', 'bs']);
    } catch {} finally { setTemplLoading(false); }
  };
  const handleTemplSubmit = async () => {
    if (!templForm.naziv.trim() || !templForm.poruka.trim()) { toast.error('Unesite naziv i poruku'); return; }
    try {
      if (editingTemplId) { await api.put(`/settings/templates/${editingTemplId}`, templForm); toast.success('Šablon ažuriran'); }
      else { await api.post('/settings/templates', templForm); toast.success('Šablon kreiran'); }
      setShowTemplForm(false); setEditingTemplId(null); setTemplForm({ naziv: '', naslov: '', poruka: '', kanal: 'email', jezik: 'sr' }); fetchTemplates();
    } catch { toast.error('Greška'); }
  };
  const handleTemplEdit = (t) => { setEditingTemplId(t.id); setTemplForm({ naziv: t.naziv, naslov: t.naslov || t.subject || '', poruka: t.poruka || t.sadrzaj || '', kanal: t.kanal || t.tip || 'email', jezik: t.jezik || 'sr' }); setShowTemplForm(true); };
  const handleTemplDelete = async (id) => { if (!(await confirmDialog('Obrisati šablon?'))) return; try { await api.delete(`/settings/templates/${id}`); toast.success('Obrisano'); fetchTemplates(); } catch { toast.error('Greška'); } };

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      const response = await api.put('/settings/email', emailForm);
      toast.success(response.data.message);
      if (response.data.connection_test === false) {
        toast.error('Konekcija sa Gmail serverom neuspjesna. Provjerite podatke.', { duration: 6000 });
      }
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greska pri cuvanju');
    } finally { setSavingEmail(false); }
  };

  const handleSaveViber = async () => {
    setSavingViber(true);
    try {
      const response = await api.put('/settings/viber', viberForm);
      toast.success(response.data.message);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greska pri cuvanju');
    } finally { setSavingViber(false); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await api.post('/settings/test-email');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greska pri slanju test emaila');
    } finally { setTestingEmail(false); }
  };

  const resetUserForm = () => {
    setUserForm({ name: '', email: '', password: '', role: 'employee' });
    setEditingUser(null);
    setShowUserForm(false);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({ name: user.name, email: user.email, password: '', role: user.role });
    setShowUserForm(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast.error('Ime i email su obavezni');
      return;
    }
    if (!editingUser && !userForm.password.trim()) {
      toast.error('Lozinka je obavezna za novog korisnika');
      return;
    }

    setSavingUser(true);
    try {
      if (editingUser) {
        const updateData = { name: userForm.name, email: userForm.email, role: userForm.role };
        if (userForm.password.trim()) updateData.password = userForm.password;
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('Korisnik azuriran');
      } else {
        await api.post('/auth/register', userForm);
        toast.success('Korisnik kreiran');
      }
      resetUserForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greska pri cuvanju korisnika');
    } finally { setSavingUser(false); }
  };

  const handleDeleteUser = async (user) => {
    if (!(await confirmDialog(`Da li ste sigurni da zelite obrisati korisnika "${user.name}"?`))) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success('Korisnik obrisan');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greska pri brisanju');
    }
  };

  const roleLabels = { admin: 'Administrator', supervizor: 'Supervizor', employee: 'Zaposleni' };
  const roleColors = {
    admin: 'bg-red-100 text-red-700',
    supervizor: 'bg-amber-100 text-amber-700',
    employee: 'bg-blue-100 text-blue-700'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-slate-500">Ucitavanje...</p></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h3 className="text-lg font-medium text-slate-900">Podesavanja</h3>
        <p className="text-sm text-slate-600 mt-1">Upravljanje integracjama i korisnicima sistema</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit flex-wrap" data-testid="settings-tabs">
        <button
          onClick={() => setActiveTab('agency')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'agency' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="tab-agency"
        >
          <ShieldCheck size={16} className="inline mr-2" />
          Moja agencija
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'integrations' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="tab-integrations"
        >
          <Envelope size={16} className="inline mr-2" />
          Integracije
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'users' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="tab-users"
        >
          <Users size={16} className="inline mr-2" />
          Korisnici
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'config' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="tab-config"
        >
          <ListBullets size={16} className="inline mr-2" />
          Konfiguracija
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'templates' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="tab-templates"
        >
          <Envelope size={16} className="inline mr-2" />
          Šabloni poruka
        </button>
      </div>

      {activeTab === 'agency' && (
        <Card className="p-6 space-y-5" data-testid="agency-form">
          <div>
            <h4 className="font-semibold text-slate-900 text-base">Podaci tvoje agencije</h4>
            <p className="text-xs text-slate-500 mt-1">Ovi podaci se automatski koriste za potpis svih emailova (rođendanske čestitke, rješenja, podsjetnici, itd.) i u generisanim dokumentima.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Naziv agencije *</Label>
              <Input value={agencyForm.naziv} onChange={e => setAgencyForm({...agencyForm, naziv: e.target.value})} placeholder="npr. FINANCE GROUP DOO" className="bg-white border-slate-300" data-testid="agency-naziv-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Email agencije</Label>
              <Input type="email" value={agencyForm.email} onChange={e => setAgencyForm({...agencyForm, email: e.target.value})} placeholder="info@vasaagencija.me" className="bg-white border-slate-300" data-testid="agency-email-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Adresa</Label>
              <Input value={agencyForm.adresa} onChange={e => setAgencyForm({...agencyForm, adresa: e.target.value})} placeholder="Bul. Skenderbeu bb" className="bg-white border-slate-300" data-testid="agency-adresa-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Grad / država / poštanski br.</Label>
              <Input value={agencyForm.grad} onChange={e => setAgencyForm({...agencyForm, grad: e.target.value})} placeholder="Ulcinj, Montenegro 85360" className="bg-white border-slate-300" data-testid="agency-grad-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Fiksni telefon</Label>
              <Input value={agencyForm.telefon} onChange={e => setAgencyForm({...agencyForm, telefon: e.target.value})} placeholder="+382 (0) 30 ..." className="bg-white border-slate-300" data-testid="agency-telefon-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Mobilni</Label>
              <Input value={agencyForm.mobilni} onChange={e => setAgencyForm({...agencyForm, mobilni: e.target.value})} placeholder="+382 (0) 69 ..." className="bg-white border-slate-300" data-testid="agency-mobilni-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">Web sajt</Label>
              <Input value={agencyForm.web} onChange={e => setAgencyForm({...agencyForm, web: e.target.value})} placeholder="www.vasaagencija.me" className="bg-white border-slate-300" data-testid="agency-web-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">PIB agencije</Label>
              <Input value={agencyForm.pib || ''} onChange={e => setAgencyForm({...agencyForm, pib: e.target.value})} placeholder="02123456" className="bg-white border-slate-300" data-testid="agency-pib-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-600">JMBG izvršnog direktora</Label>
              <Input value={agencyForm.direktor_jmbg || ''} onChange={e => setAgencyForm({...agencyForm, direktor_jmbg: e.target.value})} placeholder="1234567890123" className="bg-white border-slate-300" data-testid="agency-direktor-jmbg-input"/>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs font-semibold uppercase text-slate-600">Žiro račun agencije</Label>
              <Input value={agencyForm.ziro_racun || ''} onChange={e => setAgencyForm({...agencyForm, ziro_racun: e.target.value})} placeholder="510-XXXXXX-XX" className="bg-white border-slate-300" data-testid="agency-ziro-input"/>
            </div>
          </div>

          <div className="border-t pt-4">
            <h5 className="font-semibold text-slate-800 text-sm mb-3">Potpis u emailovima</h5>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-xs text-blue-900">
              <strong>Napomena:</strong> Ime i prezime u potpisu se automatski uzima od korisnika koji šalje email (ulogovani user). Polje "Ime i prezime potpisnika" ispod služi samo kao <em>fallback</em> za automatske notifikacije (cron-ovi: rođendani, istek tokena, istek ugovora) gdje nema ulogovanog korisnika.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase text-slate-600">Fallback ime za auto-emailove</Label>
                <Input value={agencyForm.potpisnik_ime} onChange={e => setAgencyForm({...agencyForm, potpisnik_ime: e.target.value})} placeholder="npr. Petar Petrović" className="bg-white border-slate-300" data-testid="agency-potpisnik-input"/>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase text-slate-600">Titula u potpisu</Label>
                <Input value={agencyForm.potpisnik_titula} onChange={e => setAgencyForm({...agencyForm, potpisnik_titula: e.target.value})} placeholder="ovlašćeni računovođa" className="bg-white border-slate-300" data-testid="agency-titula-input"/>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={saveAgency} disabled={savingAgency} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="agency-save-btn">
              <FloppyDisk size={18} className="mr-2" weight="bold"/>
              {savingAgency ? 'Čuvanje...' : 'Sačuvaj podatke agencije'}
            </Button>
          </div>
        </Card>
      )}

      {activeTab === 'integrations' && (
        <>
          {/* Gmail Settings */}
          <Card className="p-6" data-testid="email-settings-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-md flex items-center justify-center">
                <Envelope size={22} weight="duotone" className="text-red-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Gmail podesavanja</h4>
                <p className="text-xs text-slate-500">SMTP slanje emailova putem Google naloga</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <label className="text-xs text-slate-600">Aktivno</label>
                <input type="checkbox" checked={emailForm.enabled}
                  onChange={(e) => setEmailForm({ ...emailForm, enabled: e.target.checked })}
                  className="rounded border-slate-300" data-testid="email-enabled-checkbox"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Gmail adresa</Label>
                <Input type="email" value={emailForm.gmail_email}
                  onChange={(e) => setEmailForm({ ...emailForm, gmail_email: e.target.value })}
                  placeholder="vasa-firma@gmail.com" className="bg-white border-slate-300 text-sm" data-testid="gmail-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">App Password</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={emailForm.gmail_app_password}
                    onChange={(e) => setEmailForm({ ...emailForm, gmail_app_password: e.target.value })}
                    placeholder="xxxx xxxx xxxx xxxx" className="bg-white border-slate-300 text-sm pr-10" data-testid="gmail-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Google App Password - generisite na myaccount.google.com &rarr; Security &rarr; 2-Step Verification &rarr; App passwords. Za druge servere koristite obican mailbox password.
                </p>
              </div>

              {/* SMTP Server (Custom / Gmail) */}
              <div className="space-y-2 pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-sm font-medium text-slate-700">SMTP server (opciono — prazno = Gmail)</Label>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" onClick={() => setEmailForm({ ...emailForm, smtp_host: '', smtp_port: 0, smtp_secure: '' })}
                      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">Gmail (default)</button>
                    <button type="button" onClick={() => setEmailForm({ ...emailForm, smtp_host: 'smtp.mailhostbox.com', smtp_port: 587, smtp_secure: 'tls' })}
                      className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50">ME-net (mailhostbox)</button>
                    <button type="button" onClick={() => setEmailForm({ ...emailForm, smtp_host: 'smtp.office365.com', smtp_port: 587, smtp_secure: 'tls' })}
                      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">Office 365</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input value={emailForm.smtp_host || ''}
                    onChange={(e) => setEmailForm({ ...emailForm, smtp_host: e.target.value })}
                    placeholder="smtp.mailhostbox.com" className="bg-white border-slate-300 sm:col-span-2"
                    data-testid="smtp-host-input" />
                  <Input type="number" value={emailForm.smtp_port || ''}
                    onChange={(e) => setEmailForm({ ...emailForm, smtp_port: parseInt(e.target.value) || 0 })}
                    placeholder="587 ili 465" className="bg-white border-slate-300"
                    data-testid="smtp-port-input" />
                </div>
                <div className="flex gap-3 items-center">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="smtp_secure" value="" checked={!emailForm.smtp_secure || emailForm.smtp_secure === 'ssl'}
                      onChange={() => setEmailForm({ ...emailForm, smtp_secure: 'ssl' })} />
                    SSL (port 465)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="smtp_secure" value="tls" checked={emailForm.smtp_secure === 'tls'}
                      onChange={() => setEmailForm({ ...emailForm, smtp_secure: 'tls' })} />
                    STARTTLS (port 587)
                  </label>
                </div>
                <p className="text-xs text-slate-500">
                  Za Gmail ostavite SMTP host prazan. Za vlastiti domen (npr. financegroup.me preko ME-net) izaberite preset "ME-net".
                </p>
              </div>

              <div className="space-y-2 pt-3 border-t border-slate-200">
                <div className="text-sm font-medium text-slate-700">IMAP (cuvanje u Sent / Poslano folder webmail-a)</div>
                <p className="text-xs text-slate-500">
                  Ako je popunjen IMAP host, sve poslate poruke ce se pojavljivati u webmail Sent folderu (npr. webmail.financegroup.me &rarr; Poslano). Ako ostavite prazno, sistem ce pokusati automatski (zamijeniti "smtp." sa "imap." u SMTP hostu).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input value={emailForm.imap_host || ''}
                    onChange={(e) => setEmailForm({ ...emailForm, imap_host: e.target.value })}
                    placeholder="imap.mailhostbox.com" className="bg-white border-slate-300 sm:col-span-2"
                    data-testid="imap-host-input" />
                  <Input type="number" value={emailForm.imap_port || ''}
                    onChange={(e) => setEmailForm({ ...emailForm, imap_port: parseInt(e.target.value) || 0 })}
                    placeholder="993" className="bg-white border-slate-300"
                    data-testid="imap-port-input" />
                </div>
                <Input value={emailForm.sent_folder || ''}
                  onChange={(e) => setEmailForm({ ...emailForm, sent_folder: e.target.value })}
                  placeholder='Sent folder ime (ostavi prazno za auto: "Sent", "Poslano", "Sent Items"...)'
                  className="bg-white border-slate-300"
                  data-testid="sent-folder-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleSaveEmail} disabled={savingEmail}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="save-email-button">
                  <FloppyDisk size={16} className="mr-2" />{savingEmail ? 'Cuvanje...' : 'Sacuvaj'}
                </Button>
                <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail || !emailForm.enabled}
                  className="rounded-md px-4 py-2 font-medium" data-testid="test-email-button">
                  <PaperPlaneRight size={16} className="mr-2" />{testingEmail ? 'Slanje...' : 'Posalji test email'}
                </Button>
              </div>
            </div>
          </Card>

          {/* Viber Settings */}
          <Card className="p-6" data-testid="viber-settings-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-purple-50 rounded-md flex items-center justify-center">
                <ChatCircleDots size={22} weight="duotone" className="text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Viber Business podesavanja</h4>
                <p className="text-xs text-slate-500">Slanje poruka klijentima preko Viber-a</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <label className="text-xs text-slate-600">Aktivno</label>
                <input type="checkbox" checked={viberForm.enabled}
                  onChange={(e) => setViberForm({ ...viberForm, enabled: e.target.checked })}
                  className="rounded border-slate-300" data-testid="viber-enabled-checkbox"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Viber Auth Token</Label>
                <div className="relative">
                  <Input type={showToken ? "text" : "password"} value={viberForm.viber_auth_token}
                    onChange={(e) => setViberForm({ ...viberForm, viber_auth_token: e.target.value })}
                    placeholder="Viber Business API token" className="bg-white border-slate-300 text-sm pr-10" data-testid="viber-token-input"
                  />
                  <button type="button" onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showToken ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Infobip API Key</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Infobip Base URL</Label>
                <Input value={viberForm.infobip_base_url || ''}
                  onChange={(e) => setViberForm({ ...viberForm, infobip_base_url: e.target.value })}
                  placeholder="https://xxxxx.api.infobip.com" className="bg-white border-slate-300 text-sm" data-testid="viber-base-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Infobip Sender ID</Label>
                <Input value={viberForm.infobip_sender || ''}
                  onChange={(e) => setViberForm({ ...viberForm, infobip_sender: e.target.value })}
                  placeholder="IBSelfServe" className="bg-white border-slate-300 text-sm" data-testid="viber-sender-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Ime posiljaoca</Label>
                <Input value={viberForm.viber_sender_name}
                  onChange={(e) => setViberForm({ ...viberForm, viber_sender_name: e.target.value })}
                  placeholder="FINANCE GROUP" className="bg-white border-slate-300 text-sm" data-testid="viber-sender-input"
                />
              </div>

              {/* Pause switch — temporarily disable all Viber sending */}
              <div className={`flex items-start gap-3 p-3 rounded-md border ${viberForm.paused ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
                <input type="checkbox" id="viber-paused" checked={!!viberForm.paused}
                  onChange={(e) => setViberForm({ ...viberForm, paused: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-amber-600" data-testid="viber-paused-toggle" />
                <label htmlFor="viber-paused" className="text-sm cursor-pointer flex-1">
                  <span className="font-semibold text-slate-800">Pauziraj Viber kanal</span>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                    Privremeno onesposobi slanje Viber poruka iz CRM-a (npr. dok čekamo aktivaciju sender-a od Infobip-a).
                    Svaki pokušaj slanja vraća jasnu poruku korisniku; nijedna poruka ne odlazi na Infobip API,
                    pa ne troše se rejects niti pare. <strong>Email kanal nastavlja da radi normalno.</strong>
                  </p>
                  {viberForm.paused && (
                    <p className="text-xs font-semibold text-amber-700 mt-2">⚠️ TRENUTNO PAUZIRANO — ne zaboravite isključiti kad sender bude aktiviran.</p>
                  )}
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSaveViber} disabled={savingViber}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="save-viber-button">
                  <FloppyDisk size={16} className="mr-2" />{savingViber ? 'Cuvanje...' : 'Sacuvaj'}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-slate-900">Upravljanje korisnicima</h4>
              <p className="text-xs text-slate-500 mt-1">Kreirajte, uredite ili obrisite korisnike sistema</p>
            </div>
            <Button onClick={() => { resetUserForm(); setShowUserForm(true); }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="add-user-button">
              <UserCirclePlus size={18} className="mr-2" />Novi korisnik
            </Button>
          </div>

          {/* User Form Modal */}
          {showUserForm && (
            <Card className="p-6 border-blue-200 bg-blue-50/30" data-testid="user-form-card">
              <h4 className="font-medium text-slate-900 mb-4">
                {editingUser ? `Uredi korisnika: ${editingUser.name}` : 'Novi korisnik'}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Ime i prezime</Label>
                  <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    placeholder="Ime Prezime" className="bg-white border-slate-300 text-sm" data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Email</Label>
                  <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="korisnik@financegroup.me" className="bg-white border-slate-300 text-sm" data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                    {editingUser ? 'Nova lozinka (ostavite prazno za postojecu)' : 'Lozinka'}
                  </Label>
                  <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder={editingUser ? 'Ostavite prazno...' : 'Lozinka'} className="bg-white border-slate-300 text-sm" data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Rola</Label>
                  <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="user-role-select">
                    <option value="employee">Zaposleni</option>
                    <option value="supervizor">Supervizor</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button onClick={handleSaveUser} disabled={savingUser}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="save-user-button">
                  <FloppyDisk size={16} className="mr-2" />{savingUser ? 'Cuvanje...' : 'Sacuvaj'}
                </Button>
                <Button variant="outline" onClick={resetUserForm} className="rounded-md px-4 py-2 font-medium" data-testid="cancel-user-button">
                  Otkazi
                </Button>
              </div>
            </Card>
          )}

          {/* Users List */}
          {usersLoading ? (
            <div className="flex items-center justify-center h-32"><p className="text-slate-500">Ucitavanje...</p></div>
          ) : (
            <div className="space-y-2" data-testid="users-list">
              {users.map((user) => (
                <Card key={user.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`user-row-${user.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-sm font-bold text-slate-600">
                          {user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 text-sm" data-testid={`user-name-${user.id}`}>{user.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColors[user.role] || 'bg-slate-100 text-slate-600'}`}>
                            {(user.role === 'admin' || user.role === 'super_admin') && <ShieldCheck size={12} className="inline mr-1" />}
                            {roleLabels[user.role] || user.role}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">{user.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEditUser(user)}
                        className="text-slate-500 hover:text-blue-600" data-testid={`edit-user-${user.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user)}
                        className="text-slate-500 hover:text-red-600" data-testid={`delete-user-${user.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div className="space-y-6" data-testid="config-tab">
          {configLoading ? <p className="text-center text-slate-500 py-8">Učitavanje...</p> : (
            <>
              {[
                { key: 'vrste_dokumenata', label: 'Vrste dokumenata', desc: 'Tipovi dokumenata u sistemu' },
                { key: 'vrste_posla', label: 'Vrste posla', desc: 'Kategorije posla u Dnevniku rada' },
                { key: 'vjere', label: 'Vjeroispovijesti', desc: 'Za filtriranje klijenata pri slanju čestitki' },
                { key: 'nacionalnosti', label: 'Nacionalnosti', desc: 'Za filtriranje klijenata pri slanju čestitki' },
                { key: 'radna_mjesta', label: 'Radna mjesta', desc: 'Lista radnih mjesta za ugovore o radu i prijavu radnika' },
                { key: 'irms_predmeti', label: 'IRMS — Naziv predmeta', desc: 'Lista naziva predmeta za IRMS Zahtjeve' },
              ].map(({ key, label, desc }) => (
                <Card key={key} className="p-5" data-testid={`config-${key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800">{label}</h4>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(configLists[key] || []).map((item, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-slate-100 text-slate-700 border border-slate-200">
                        {item}
                        <button onClick={() => removeConfigItem(key, idx)} className="ml-1 hover:text-red-600 transition-colors" data-testid={`remove-${key}-${idx}`}>
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                    {(configLists[key] || []).length === 0 && <span className="text-xs text-slate-400">Nema stavki</span>}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newItems[key] || ''}
                      onChange={e => setNewItems(prev => ({ ...prev, [key]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && addConfigItem(key)}
                      placeholder={`Dodaj ${label.toLowerCase()}...`}
                      className="bg-white border-slate-300 text-sm flex-1"
                      data-testid={`input-${key}`}
                    />
                    <Button onClick={() => addConfigItem(key)} variant="outline" size="sm" className="shrink-0" data-testid={`add-${key}`}>
                      <Plus size={14} className="mr-1" /> Dodaj
                    </Button>
                  </div>
                </Card>
              ))}
              <Button onClick={saveConfigLists} disabled={savingConfig} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-config-btn">
                <FloppyDisk size={16} className="mr-2" /> {savingConfig ? 'Čuvam...' : 'Sačuvaj sve'}
              </Button>
            </>
          )}
        </div>
      )}
      {activeTab === 'templates' && (
        <div className="space-y-4" data-testid="templates-tab">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">Šabloni poruka za email i Viber po jezicima</p>
            <Button onClick={() => { setEditingTemplId(null); setTemplForm({ naziv: '', naslov: '', poruka: '', kanal: 'email', jezik: 'sr' }); setShowTemplForm(true); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-template-btn">
              <Plus size={16} className="mr-2" /> Novi šablon
            </Button>
          </div>

          {showTemplForm && (
            <Card className="p-5 border-blue-200 bg-blue-50/30" data-testid="template-form">
              <h4 className="text-sm font-semibold mb-3">{editingTemplId ? 'Uredi šablon' : 'Novi šablon'}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Naziv šablona *</Label><Input value={templForm.naziv} onChange={e => setTemplForm({...templForm, naziv: e.target.value})} placeholder="npr: Podsjetnik za dokumentaciju" className="bg-white border-slate-300 text-sm" data-testid="tmpl-naziv" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Naslov emaila *</Label><Input value={templForm.naslov} onChange={e => setTemplForm({...templForm, naslov: e.target.value})} placeholder="npr: Podsjetnik - Dostava dokumentacije" className="bg-white border-slate-300 text-sm" data-testid="tmpl-naslov" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Kanal</Label>
                  <Select value={templForm.kanal} onValueChange={v => setTemplForm({...templForm, kanal: v})}><SelectTrigger className="bg-white border-slate-300 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="viber">Viber</SelectItem><SelectItem value="oba">Email + Viber</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Jezik</Label>
                  <Select value={templForm.jezik} onValueChange={v => setTemplForm({...templForm, jezik: v})}><SelectTrigger className="bg-white border-slate-300 text-sm"><SelectValue /></SelectTrigger><SelectContent>{cfgJezici.map(j => <SelectItem key={j} value={j.toLowerCase().substring(0,2)}>{j}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="space-y-1 mt-3"><Label className="text-xs text-slate-500">Poruka *</Label><textarea value={templForm.poruka} onChange={e => setTemplForm({...templForm, poruka: e.target.value})} placeholder="Tekst poruke..." className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="tmpl-poruka" /></div>
              <div className="flex gap-2 mt-3"><Button onClick={handleTemplSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tmpl-save-btn">{editingTemplId ? 'Ažuriraj' : 'Kreiraj'}</Button><Button variant="outline" onClick={() => setShowTemplForm(false)}>Odustani</Button></div>
            </Card>
          )}

          {templLoading ? <p className="text-center text-slate-500 py-8">Učitavanje...</p> : templates.length === 0 && !showTemplForm ? (
            <Card className="p-8 text-center"><Envelope size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nema šablona poruka</p></Card>
          ) : (
            <div className="space-y-3">
              {templates.map(t => (
                <Card key={t.id} className="p-4" data-testid={`template-${t.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-slate-800">{t.naziv}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">Naslov: {t.naslov || t.subject || '-'}</p>
                      <p className="text-sm text-slate-600 mt-2">{t.poruka || t.sadrzaj || ''}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">{t.kanal || t.tip || 'email'}</span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">{t.jezik || 'sr'}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-3">
                      <button onClick={() => handleTemplEdit(t)} className="p-2 hover:bg-blue-100 rounded"><PencilSimple size={16} className="text-blue-600" /></button>
                      <button onClick={() => handleTemplDelete(t.id)} className="p-2 hover:bg-red-100 rounded"><Trash size={16} className="text-red-600" /></button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
